import CreateArticle from '@/views/CreateArticle.vue';
import CreateEvent from '@/views/CreateEvent.vue';

import { createRouter, createWebHistory } from "vue-router";
import DashboardView from "@/views/DashboardView.vue";
import Login from "@/views/Login.vue";
import Logout from "@/views/LogOut.vue";
import ProjectDetails from "@/views/ProjectDetails.vue";
import UserDetails from "@/views/UserDetails.vue";
import UserList from "@/views/UserList.vue";
import TalentManagement from "@/views/TalentManagement.vue";
import Manager from "@/views/Manager.vue";
import TalentProfileComponent from "@/components/talents/TalentProfileComponent.vue";
import Projects from "@/views/Projects.vue";
import Unauthorized from "@/components/Unauthorized.vue";
import NotFound from "@/views/NotFound.vue";
import Settings from "@/views/Settings.vue";
import ForgotPassword from "@/views/ForgotPassword.vue";
import ResetPassword from "@/views/ResetPassword.vue";
import ActivityData from "@/views/ActivityData.vue";
import Profile from "@/views/Profile.vue";
import TalentDetails from "@/views/TalentDetails.vue";
import Achievements from "@/views/Achievements.vue";
import ActivityDump from "@/views/ActivityDump.vue";
import JobListView from "@/views/JobListView.vue";
import store from "@/store";

// Define role-checking function
const checkRole = (allowedRoles) => (to, from, next) => {
  const roleFromStorage = localStorage.getItem("role");
  const roleFromStore = store.state.role;
  const userRole = roleFromStore || roleFromStorage;

  const tokenFromStorage = localStorage.getItem("token");
  const tokenFromStore = store.state.token;
  const userFromStore = store.state.user;
  const isAuthenticated =
    (tokenFromStorage && tokenFromStore && userFromStore) ||
    (tokenFromStorage && userFromStore);

  if (!isAuthenticated) {
    next({ name: "Login" });
  } else if (allowedRoles.includes(userRole)) {
    next();
  } else {
    next({ name: "Unauthorized" });
  }
};

const routes = [
  { path: "/login", name: "Login", component: Login },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: ForgotPassword,
  },
  { path: "/reset-password", name: "ResetPassword", component: ResetPassword },

  { path: "/logout", name: "Logout", component: Logout },
  {
    path: "/users",
    name: "UserList",
    component: UserList,
    meta: { requiresAuth: true, label: "Users" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/users/:userId",
    name: "UserDetails",
    component: UserDetails,
    meta: { requiresAuth: true, label: "Users" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/managers",
    name: "Managers",
    component: Manager,
    meta: { requiresAuth: true, label: "Managers" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/talents",
    name: "Talents",
    component: TalentManagement,
    meta: { requiresAuth: true, label: "Talents" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/talents/:talentId",
    name: "TalentProfile",
    component: TalentProfileComponent,
    props: true,
    meta: { requiresAuth: true, label: "Talent" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: '/articles/create',
    name: 'CreateArticle',
    component: CreateArticle,
    meta: { requiresAuth: true, label: 'Create Article' },
    beforeEnter: checkRole(['admin'])
  },
  {
    path: '/events/create',
    name: 'CreateEvent',
    component: CreateEvent,
    meta: { requiresAuth: true, label: 'Create Event' },
    beforeEnter: checkRole(['admin'])
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardView,
    meta: { requiresAuth: true, label: "Home" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/projects",
    name: "Projects",
    component: Projects,
    meta: { requiresAuth: true, label: "Projects" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/achievements",
    name: "Achievements",
    component: Achievements,
    meta: { requiresAuth: true, label: "Achievements" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/projects/:projectId",
    name: "ProjectDetails",
    component: ProjectDetails,
    meta: { requiresAuth: true, label: "Projects" },
    beforeEnter: checkRole(["admin"]),
  },
  { path: "/unauthorized", name: "Unauthorized", component: Unauthorized },
  { path: "/:pathMatch(.*)*", name: "NotFound", component: NotFound },
  {
    path: "/settings",
    name: "Settings",
    component: Settings,
    meta: { requiresAuth: true, label: "Settings" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/profile",
    name: "Profile",
    component: Profile,
    meta: { requiresAuth: true, label: "My Profile" },
    beforeEnter: checkRole(["admin"]),
  },

  {
    path: "/projects/:projectId/activities",
    name: "ActivityData",
    component: ActivityData,
    props: true,
    meta: { requiresAuth: true, label: "Projects" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/talents/:talentId",
    name: "TalentDetails",
    component: TalentDetails,
    meta: { requiresAuth: true, label: "Talent" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/activity-dump",
    name: "ActivityDump",
    component: ActivityDump,
    meta: { requiresAuth: true, label: "Activity Dump" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/support",
    name: "Support",
    component: () => import("@/views/Support.vue"),
    meta: { requiresAuth: true, label: "Support" },
    beforeEnter: checkRole(["admin"]),
  },
  {
    path: "/jobs",
    name: "Jobs",
    component: JobListView,
    meta: { requiresAuth: true, label: "Jobs" },
    beforeEnter: checkRole(["admin"]),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

// Global route guard for authentication and redirection
router.beforeEach((to, from, next) => {
  const tokenFromStorage = localStorage.getItem("token");
  const tokenFromStore = store.state.token;
  const userFromStore = store.state.user;

  const isAuthenticated =
    (tokenFromStorage && tokenFromStore && userFromStore) ||
    (tokenFromStorage && userFromStore);

  if (to.path === "/" && isAuthenticated) {
    next({ name: "Dashboard" });
  } else if (to.path === "/" && !isAuthenticated) {
    next({ name: "Login" });
  } else if ((to.name === "Login") && isAuthenticated) {
    next({ name: "Dashboard" });
  } else if (to.name === "Login") {
    next();
  } else if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    !isAuthenticated
  ) {
    next({ name: "Login" });
  } else {
    next();
  }
});

export default router;
